/**
 * Utilidades PURAS do "signature pad" (`AssinaturaCanvas`). Isoladas do DOM/canvas
 * para serem testáveis: só matemática dos pontos do traço. A parte que toca o
 * `<canvas>` (desenho, exportação PNG) vive no componente.
 */

export interface Ponto {
  readonly x: number;
  readonly y: number;
}

export interface Retangulo {
  readonly x: number;
  readonly y: number;
  readonly largura: number;
  readonly altura: number;
}

/**
 * Retângulo que envolve TODOS os pontos desenhados, com uma `margem` de folga —
 * base do recorte: exportamos só a área com tinta, removendo as bordas
 * transparentes para a assinatura "assentar" na linha do relatório.
 *
 * Devolve `null` quando não há pontos (nada foi desenhado). As coordenadas estão
 * no mesmo espaço dos pontos de entrada (o chamador aplica o `devicePixelRatio`).
 */
export function limitesDoTraco(pontos: readonly Ponto[], margem = 0): Retangulo | null {
  if (pontos.length === 0) {
    return null;
  }
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of pontos) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    x: minX - margem,
    y: minY - margem,
    largura: maxX - minX + margem * 2,
    altura: maxY - minY + margem * 2,
  };
}

/** Interseção de um retângulo com a moldura [0,0,largura,altura] (clamp ao canvas). */
export function recortarNaMoldura(r: Retangulo, largura: number, altura: number): Retangulo {
  const x = Math.max(0, Math.min(r.x, largura));
  const y = Math.max(0, Math.min(r.y, altura));
  const x2 = Math.max(0, Math.min(r.x + r.largura, largura));
  const y2 = Math.max(0, Math.min(r.y + r.altura, altura));
  return { x, y, largura: Math.max(1, x2 - x), altura: Math.max(1, y2 - y) };
}
